import type { HoldingUnitEvent } from '../holding-units/holding-unit-event.dto.js';
import type { HoldingUnitEventEntityType } from '../holding-units/holding-unit-event-envelope.dto.js';
import type { TareProfileDto } from '../tare-profiles/tare-profiles.dto.js';
import type { UnitProfileDto } from '../unit-profiles/unit-profiles.dto.js';
import type { SyncClientIdentity } from './sync.dto.js';
import type { HoldingUnitsCanonicalSnapshotRecord } from './holding-units-canonical-snapshot.dto.js';

export const holdingUnitSyncEventTypeValues = ['holdingUnitEventsApplied'] as const;
export type HoldingUnitSyncEventType = (typeof holdingUnitSyncEventTypeValues)[number];

export const holdingUnitSyncEntityTypeValues = ['holdingUnit'] as const;
export type HoldingUnitSyncEntityType = (typeof holdingUnitSyncEntityTypeValues)[number];

export interface HoldingUnitSyncPayloadV5 {
  schemaVersion: 5;
  events: HoldingUnitEvent[];
}

export type HoldingUnitSyncPayload = HoldingUnitSyncPayloadV5;

export const HOLDING_UNITS_CANONICAL_BOOTSTRAP_PROTOCOL_VERSION = 2 as const;
export const HOLDING_UNITS_CANONICAL_BOOTSTRAP_MODULE = 'holdingUnits' as const;
export const HOLDING_UNITS_CANONICAL_BOOTSTRAP_RESTART_ACTION = 'restart_bootstrap' as const;
export const HOLDING_UNITS_CANONICAL_BOOTSTRAP_MAX_RECORDS = 5_000 as const;
export const HOLDING_UNITS_CANONICAL_BOOTSTRAP_MAX_BYTES = 8 * 1024 * 1024;

export interface HoldingUnitsCanonicalBootstrapPosition {
  outerSequence: string;
  eventId: string;
}

/**
 * Resumable page cursor for an in-progress canonical bootstrap session.
 * This is deliberately distinct from the durable module cursor: applying a
 * page must not advance delta sync before the final bootstrap page commits.
 */
export interface HoldingUnitsCanonicalBootstrapCursor {
  protocolVersion: typeof HOLDING_UNITS_CANONICAL_BOOTSTRAP_PROTOCOL_VERSION;
  module: typeof HOLDING_UNITS_CANONICAL_BOOTSTRAP_MODULE;
  sessionId: string;
  watermark: string;
  watermarkEventId: string | null;
  after: HoldingUnitsCanonicalBootstrapPosition | null;
}

export interface HoldingUnitsCanonicalStreamAck {
  entityType: HoldingUnitEventEntityType;
  entityId: string;
  streamVersion: number;
  lastEventId: string;
}

export interface HoldingUnitsCanonicalModuleCursor {
  protocolVersion: typeof HOLDING_UNITS_CANONICAL_BOOTSTRAP_PROTOCOL_VERSION;
  module: typeof HOLDING_UNITS_CANONICAL_BOOTSTRAP_MODULE;
  generationId: string;
  outerSequence: string;
  eventId: string | null;
  pendingStreamAcks: HoldingUnitsCanonicalStreamAck[];
}

export interface HoldingUnitsCanonicalEventRecord {
  kind: 'event';
  outerSequence: string;
  event: HoldingUnitEvent;
}

export type HoldingUnitsCanonicalRecord =
  | HoldingUnitsCanonicalSnapshotRecord
  | HoldingUnitsCanonicalEventRecord;

export interface HoldingUnitsCanonicalBootstrapConfiguration {
  tareProfiles: TareProfileDto[];
  unitProfiles: UnitProfileDto[];
}

export interface HoldingUnitsCanonicalBootstrapRequest {
  client: SyncClientIdentity;
  cursor: HoldingUnitsCanonicalBootstrapCursor | null;
}

/**
 * Structured ApiError details for bootstrap progress that the server can no
 * longer resume. A client may restart only after discarding its rebuildable
 * partial bootstrap state; this signal never applies to delta cursors.
 */
export interface HoldingUnitsCanonicalBootstrapRestartRequiredDetails {
  module: typeof HOLDING_UNITS_CANONICAL_BOOTSTRAP_MODULE;
  action: typeof HOLDING_UNITS_CANONICAL_BOOTSTRAP_RESTART_ACTION;
  sessionId: string;
}

export interface HoldingUnitsCanonicalBootstrapResponse {
  protocolVersion: typeof HOLDING_UNITS_CANONICAL_BOOTSTRAP_PROTOCOL_VERSION;
  module: typeof HOLDING_UNITS_CANONICAL_BOOTSTRAP_MODULE;
  sessionId: string;
  watermark: string;
  watermarkEventId: string | null;
  configuration: HoldingUnitsCanonicalBootstrapConfiguration | null;
  records: HoldingUnitsCanonicalRecord[];
  nextCursor: HoldingUnitsCanonicalBootstrapCursor | null;
  moduleCursor: HoldingUnitsCanonicalModuleCursor | null;
  complete: boolean;
}

export interface HoldingUnitsCanonicalStreamCatchup {
  entityType: HoldingUnitEventEntityType;
  entityId: string;
  snapshot: HoldingUnitsCanonicalSnapshotRecord | null;
  tail: HoldingUnitsCanonicalEventRecord[];
}

export interface HoldingUnitsCanonicalDeltaRequest {
  client: SyncClientIdentity;
  cursor: HoldingUnitsCanonicalModuleCursor;
}

export interface HoldingUnitsCanonicalDeltaResponse {
  protocolVersion: typeof HOLDING_UNITS_CANONICAL_BOOTSTRAP_PROTOCOL_VERSION;
  module: typeof HOLDING_UNITS_CANONICAL_BOOTSTRAP_MODULE;
  streamCatchups: HoldingUnitsCanonicalStreamCatchup[];
  records: HoldingUnitsCanonicalEventRecord[];
  nextCursor: HoldingUnitsCanonicalModuleCursor;
  hasMore: boolean;
}
